import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Trash2, Plus, Minus, ShoppingBag, Tag, Check, AlertCircle, User, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useToast } from './Toast';
import MagneticButton, { MagneticLabel } from './MagneticButton';
import { merkeKaufSprache } from './kauf/KaufHinweis';
import { zustimmungFelder } from './kauf/zustimmung';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ────────────────────────────────────────────────────────────────────
   Motion presets — defined once so the entrance reads as a single
   tempo. Easing curve matches the rest of the site ([0.22, 1, …]),
   sidebar uses a slightly snappier glide so it lands fast but soft.
   ──────────────────────────────────────────────────────────────── */
const EASE = [0.22, 1, 0.36, 1];

const backdropAnim = {
  hidden: { opacity: 0 },
  show:   { opacity: 1, transition: { duration: 0.25 } },
  exit:   { opacity: 0, transition: { duration: 0.25 } },
};

const sidebarAnim = {
  hidden: { x: '100%' },
  show:   { x: 0,      transition: { duration: 0.55, ease: [0.32, 0.72, 0, 1] } },
  exit:   { x: '100%', transition: { duration: 0.45, ease: EASE } },
};

// Stagger orchestrator for the sidebar's inner content — header,
// items, footer fan in once the panel has begun gliding in.
const innerStagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.07, delayChildren: 0.2 } },
};

const headerAnim = {
  hidden: { opacity: 0, y: -16 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

const itemAnim = {
  hidden: { opacity: 0, y: 24, filter: 'blur(6px)' },
  show:   { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.45, ease: EASE } },
  exit:   { opacity: 0, x: 40, scale: 0.95, transition: { duration: 0.22 } },
};

const footerAnim = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

// Pflicht-Häkchen im Stil der Seite: dunkles Kästchen, angehakt mit orangem Rand und orangem Haken
// (statt des weißen Browser-Kästchens). Das echte <input> bleibt für Tastatur, Screenreader und Tests.
const Haekchen = ({ checked, onChange, testId }) => (
  <span className="relative mt-px h-[18px] w-[18px] shrink-0">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="peer absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none rounded-[5px] border border-white/20 bg-white/[0.04] transition-colors duration-200 hover:border-white/40 checked:border-[#F97316] checked:bg-[#F97316]/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F97316]/50"
      data-testid={testId}
    />
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#F97316"
      strokeWidth="3.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="pointer-events-none absolute inset-0 m-auto h-3 w-3 opacity-0 transition-opacity duration-200 peer-checked:opacity-100"
    >
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  </span>
);

const CartSidebar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isEnglish = i18n.language === 'en' || location.pathname.startsWith('/en');
  // Preise wie auf der restlichen Seite: deutsch „14,99 €“, englisch „€14.99“
  const preis = (n) => (isEnglish ? `€${n.toFixed(2)}` : `${n.toFixed(2).replace('.', ',')} €`);
  const { notify } = useToast();
  const langPrefix = isEnglish ? '/en' : '';

  const {
    cartItems,
    isCartOpen,
    discountCode,
    discountInfo,
    setDiscountCode,
    setDiscountInfo,
    removeFromCart,
    updateQuantity,
    updateLicenseType,
    clearCart,
    getItemPrice,
    getSubtotal,
    getDiscountAmount,
    getTotal,
    closeCart
  } = useCart();

  const [isValidating, setIsValidating] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  // § 356 Abs. 6 Nr. 2 BGB: explicit consent to begin performance (= start the download)
  // before the withdrawal period expires, with acknowledgment that the consent
  // extinguishes the right of withdrawal. Without this, customers get a 14-day
  // withdrawal window even after downloading.
  const [waiveWithdrawal, setWaiveWithdrawal] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Auto-validate discount code when cart opens and code is set (e.g., from product page)
  useEffect(() => {
    if (isCartOpen && discountCode && !discountInfo && !isValidating) {
      validateDiscountCodeAuto();
    }
  }, [isCartOpen, discountCode]);

  const validateDiscountCodeAuto = async () => {
    if (!discountCode.trim()) return;

    setIsValidating(true);
    setDiscountError('');

    try {
      const response = await fetch(
        `${API_URL}/api/discount-codes/validate-cart?code=${encodeURIComponent(discountCode)}`
      );

      if (response.ok) {
        const data = await response.json();
        setDiscountInfo(data);
        setDiscountError('');
      } else {
        setDiscountInfo(null);
        setDiscountError(isEnglish ? 'Invalid discount code' : 'Ungültiger Rabattcode');
      }
    } catch (error) {
      setDiscountInfo(null);
      setDiscountError(isEnglish ? 'Error validating code' : 'Fehler beim Validieren');
    } finally {
      setIsValidating(false);
    }
  };

  const handleBrowseShop = () => {
    closeCart();
    navigate(`${langPrefix}/shop`);
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountInfo(null);
      setDiscountError('');
      return;
    }

    setIsValidating(true);
    setDiscountError('');

    try {
      const response = await fetch(
        `${API_URL}/api/discount-codes/validate-cart?code=${encodeURIComponent(discountCode)}`
      );

      if (response.ok) {
        const data = await response.json();
        setDiscountInfo(data);
        setDiscountError('');
      } else {
        setDiscountInfo(null);
        setDiscountError(isEnglish ? 'Invalid discount code' : 'Ungültiger Rabattcode');
      }
    } catch (error) {
      setDiscountInfo(null);
      setDiscountError(isEnglish ? 'Error validating code' : 'Fehler beim Validieren');
    } finally {
      setIsValidating(false);
    }
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;

    setIsCheckingOut(true);
    // Stripe schickt immer auf die deutsche Adresse zurück — KaufHinweis springt dann auf /en, wenn es englisch war
    merkeKaufSprache(isEnglish);

    try {
      const affiliateCode = sessionStorage.getItem('affiliate_code');

      const response = await fetch(`${API_URL}/api/stripe/create-cart-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cartItems.map(item => ({
            product_id: item.productId,
            license_type: item.licenseType,
            quantity: item.quantity
          })),
          discount_code: discountInfo ? discountCode : null,
          affiliate_code: affiliateCode || null,
          origin_url: window.location.origin,
          // Nachweis der beiden Pflicht-Häkchen (server.py speichert ihn an der Bestellung, Kauf-Mail bestätigt ihn)
          ...zustimmungFelder({ agb: acceptTerms, verzicht: waiveWithdrawal, en: isEnglish }),
        })
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        notify(isEnglish ? 'Checkout error. Please try again.' : 'Checkout-Fehler. Bitte versuche es erneut.', 'error');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(isEnglish ? 'Checkout error. Please try again.' : 'Checkout-Fehler. Bitte versuche es erneut.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const cartCount = cartItems.reduce((c, i) => c + i.quantity, 0);

  return (
    <AnimatePresence>
      {isCartOpen && (
        <React.Fragment key="cart-stack">
          {/* Backdrop — fades + blurs in over the page. Click anywhere
              outside the sidebar to close. */}
          <motion.div
            key="cart-backdrop"
            variants={backdropAnim}
            initial="hidden"
            animate="show"
            exit="exit"
            className="fixed inset-0 z-[9990]"
            style={{
              background: 'rgba(3, 3, 5, 0.72)',
              backdropFilter: 'blur(10px) saturate(140%)',
              WebkitBackdropFilter: 'blur(10px) saturate(140%)',
            }}
            onClick={closeCart}
          />

          {/* Sidebar — slides in from the right edge of the viewport.
              The decorative orange glow blobs anchor the brand colour
              into the panel so it stops feeling like a generic drawer. */}
          <motion.aside
            key="cart-sidebar"
            variants={sidebarAnim}
            initial="hidden"
            animate="show"
            exit="exit"
            className="fixed top-0 right-0 h-full w-full max-w-md z-[9991] flex flex-col overflow-hidden"
            style={{
              background:
                'linear-gradient(180deg, rgba(14,14,18,0.96) 0%, rgba(8,8,11,0.96) 100%)',
              backdropFilter: 'blur(40px) saturate(150%)',
              WebkitBackdropFilter: 'blur(40px) saturate(150%)',
              borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow:
                '-30px 0 80px rgba(0, 0, 0, 0.6), inset 0 1px 1px rgba(255, 255, 255, 0.04)',
            }}
            data-testid="cart-sidebar"
          >
            {/* Orange glow blob — top-right corner accent. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(249,115,22,0.28) 0%, transparent 70%)',
                filter: 'blur(24px)',
              }}
            />
            {/* Mirror blob — bottom-left, softer. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-32 -left-24 w-80 h-80 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(249,115,22,0.14) 0%, transparent 70%)',
                filter: 'blur(28px)',
              }}
            />
            {/* Top hairline accent — subtle orange streak along the
                very top edge to mark the panel as "ours". */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute top-0 left-0 right-0 h-[2px]"
              style={{
                background:
                  'linear-gradient(90deg, transparent 0%, rgba(249,115,22,0.5) 50%, transparent 100%)',
              }}
            />

            {/* Inner staggered content — fans in once the panel has
                started its slide. */}
            <motion.div
              variants={innerStagger}
              initial="hidden"
              animate="show"
              className="relative flex flex-1 flex-col min-h-0"
            >
              {/* Header */}
              <motion.div
                variants={headerAnim}
                className="flex items-center justify-between p-6 relative"
                style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}
              >
                <div className="flex items-center gap-3">
                  {/* Icon chip with orange glow halo */}
                  <div className="relative">
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background:
                          'radial-gradient(circle, rgba(249,115,22,0.5) 0%, transparent 65%)',
                        filter: 'blur(8px)',
                      }}
                      aria-hidden="true"
                    />
                    <div
                      className="relative w-10 h-10 rounded-full flex items-center justify-center"
                      style={{
                        background:
                          'linear-gradient(135deg, rgba(249,115,22,0.18) 0%, rgba(249,115,22,0.06) 100%)',
                        border: '1px solid rgba(249,115,22,0.35)',
                      }}
                    >
                      <ShoppingBag
                        className="w-5 h-5"
                        style={{ color: 'var(--primary)' }}
                      />
                    </div>
                  </div>
                  <div>
                    <h2
                      className="font-manrope text-xl font-extrabold tracking-tight"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {isEnglish ? 'Cart' : 'Warenkorb'}
                    </h2>
                    <p
                      className="font-mono text-[10px] uppercase tracking-[0.25em]"
                      style={{ color: '#8f8f99' }}
                    >
                      {cartCount === 0
                        ? (isEnglish ? 'empty' : 'leer')
                        : `${cartCount} ${
                            cartCount === 1
                              ? (isEnglish ? 'item' : 'artikel')
                              : (isEnglish ? 'items' : 'artikel')
                          }`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeCart}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 liquid-glass"
                  data-testid="cart-close-btn"
                  aria-label={isEnglish ? 'Close cart' : 'Warenkorb schließen'}
                >
                  <X
                    className="w-5 h-5"
                    style={{ color: 'var(--text-primary)' }}
                  />
                </button>
              </motion.div>

              {/* Items list — empty state OR animated stack of items */}
              <div className="flex-1 overflow-y-auto p-6 space-y-3">
                {cartItems.length === 0 ? (
                  <motion.div
                    variants={itemAnim}
                    className="flex flex-col items-center justify-center h-full text-center px-4"
                  >
                    {/* Empty-state icon with a soft pulsing glow ring */}
                    <div className="relative mb-6">
                      <motion.div
                        animate={{
                          scale: [1, 1.15, 1],
                          opacity: [0.35, 0.6, 0.35],
                        }}
                        transition={{
                          duration: 2.6,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                        className="absolute inset-0 rounded-full"
                        style={{
                          background:
                            'radial-gradient(circle, rgba(249,115,22,0.35) 0%, transparent 65%)',
                          filter: 'blur(14px)',
                        }}
                        aria-hidden="true"
                      />
                      <div
                        className="relative w-20 h-20 rounded-full flex items-center justify-center"
                        style={{
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.08)',
                        }}
                      >
                        <ShoppingBag
                          className="w-8 h-8"
                          style={{ color: 'var(--primary)' }}
                        />
                      </div>
                    </div>
                    <p
                      className="font-manrope text-lg font-bold mb-2"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {isEnglish ? 'Your cart is empty' : 'Dein Warenkorb ist leer'}
                    </p>
                    <p
                      className="font-manrope text-sm mb-6 max-w-[260px]"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {isEnglish
                        ? 'Pick up an extension or two and they will land here.'
                        : 'Schnapp dir ein paar Extensions — sie landen hier.'}
                    </p>
                    <button
                      onClick={handleBrowseShop}
                      className="px-6 py-3 rounded-xl font-manrope font-bold text-sm text-[#1a0b02] hover:scale-[1.03] transition-transform duration-300"
                      style={{
                        background: 'var(--primary)',
                        boxShadow: '0 0 30px rgba(249,115,22,0.35)',
                      }}
                      data-testid="browse-shop-btn"
                    >
                      {isEnglish ? 'Browse tools' : 'Werkzeuge ansehen'}
                    </button>
                  </motion.div>
                ) : (
                  <AnimatePresence mode="popLayout" initial={false}>
                    {cartItems.map((item) => (
                      <motion.div
                        key={`${item.productId}-${item.licenseType}`}
                        variants={itemAnim}
                        layout
                        exit="exit"
                        className="group relative p-4 rounded-2xl overflow-hidden transition-shadow duration-300 hover:shadow-[0_0_28px_rgba(249,115,22,0.12)]"
                        style={{
                          background:
                            'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        {/* Hover spotlight — fades in on hover. */}
                        <div
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          style={{
                            background:
                              'radial-gradient(220px circle at 80% 20%, rgba(249,115,22,0.12), transparent 60%)',
                          }}
                        />

                        <div className="relative flex gap-4">
                          {/* Product Image */}
                          <div
                            className="w-20 h-14 rounded-xl overflow-hidden flex-shrink-0"
                            style={{
                              boxShadow:
                                '0 4px 16px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
                            }}
                          >
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                          </div>

                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h3
                                className="font-manrope font-bold text-sm truncate"
                                style={{ color: 'var(--text-primary)' }}
                              >
                                {item.name}
                              </h3>
                              <button
                                onClick={() =>
                                  removeFromCart(item.productId, item.licenseType)
                                }
                                className="transition-colors flex-shrink-0 hover:text-red-400"
                                style={{ color: 'var(--text-muted)' }}
                                data-testid={`remove-item-${item.productId}`}
                                aria-label={isEnglish ? 'Remove item' : 'Entfernen'}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            {/* License Type Selector — ausgeblendet, es gibt
                                nur noch die Einzellizenz (siehe ProductDetail). */}
                            <div className="flex gap-2 mt-2" style={{ display: 'none' }}>
                              <button
                                onClick={() =>
                                  updateLicenseType(
                                    item.productId,
                                    item.licenseType,
                                    'single'
                                  )
                                }
                                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-manrope transition-all duration-300"
                                style={{
                                  background:
                                    item.licenseType === 'single'
                                      ? 'rgba(249, 115, 22, 0.18)'
                                      : 'rgba(255, 255, 255, 0.05)',
                                  border: `1px solid ${
                                    item.licenseType === 'single'
                                      ? 'rgba(249, 115, 22, 0.5)'
                                      : 'var(--glass-border)'
                                  }`,
                                  color:
                                    item.licenseType === 'single'
                                      ? 'var(--primary)'
                                      : 'var(--text-secondary)',
                                }}
                              >
                                <User className="w-3 h-3" />
                                Single
                              </button>
                              <button
                                onClick={() =>
                                  updateLicenseType(
                                    item.productId,
                                    item.licenseType,
                                    'team'
                                  )
                                }
                                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-manrope transition-all duration-300"
                                style={{
                                  background:
                                    item.licenseType === 'team'
                                      ? 'rgba(249, 115, 22, 0.18)'
                                      : 'rgba(255, 255, 255, 0.05)',
                                  border: `1px solid ${
                                    item.licenseType === 'team'
                                      ? 'rgba(249, 115, 22, 0.5)'
                                      : 'var(--glass-border)'
                                  }`,
                                  color:
                                    item.licenseType === 'team'
                                      ? 'var(--primary)'
                                      : 'var(--text-secondary)',
                                }}
                              >
                                <Users className="w-3 h-3" />
                                Team
                              </button>
                            </div>

                            {/* Quantity & Price */}
                            <div className="flex items-center justify-between mt-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    updateQuantity(
                                      item.productId,
                                      item.licenseType,
                                      item.quantity - 1
                                    )
                                  }
                                  className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors duration-300 hover:bg-white/10"
                                  style={{
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid var(--glass-border)',
                                    color: 'var(--text-primary)',
                                  }}
                                  data-testid={`decrease-qty-${item.productId}`}
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span
                                  className="font-manrope font-medium w-6 text-center"
                                  style={{ color: 'var(--text-primary)' }}
                                >
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() =>
                                    updateQuantity(
                                      item.productId,
                                      item.licenseType,
                                      item.quantity + 1
                                    )
                                  }
                                  className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors duration-300 hover:bg-white/10"
                                  style={{
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid var(--glass-border)',
                                    color: 'var(--text-primary)',
                                  }}
                                  data-testid={`increase-qty-${item.productId}`}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                              <span
                                className="font-manrope font-extrabold"
                                style={{ color: 'var(--text-primary)' }}
                              >
                                {preis(getItemPrice(item) * item.quantity)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>

              {/* Footer — Discount + Totals + Checkout */}
              {cartItems.length > 0 && (
                <motion.div
                  variants={footerAnim}
                  className="p-6 space-y-4 relative"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
                >
                  {/* Discount Code Input */}
                  <div>
                    <label
                      className="font-mono text-[10px] uppercase tracking-[0.25em] block mb-2"
                      style={{ color: '#8f8f99' }}
                    >
                      {isEnglish ? 'Discount Code' : 'Rabattcode'}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={discountCode}
                        onChange={(e) => {
                          setDiscountCode(e.target.value.toUpperCase());
                          setDiscountInfo(null);
                          setDiscountError('');
                        }}
                        placeholder="CODE123"
                        className="flex-1 px-4 py-2.5 rounded-xl text-sm font-manrope outline-none transition-all duration-300 focus:ring-2 focus:ring-[#F97316]/40"
                        style={{
                          background: 'rgba(255, 255, 255, 0.04)',
                          border: `1px solid ${
                            discountError
                              ? '#ef4444'
                              : discountInfo
                              ? '#22c55e'
                              : 'var(--glass-border)'
                          }`,
                          color: 'var(--text-primary)',
                        }}
                        data-testid="discount-code-input"
                      />
                      <button
                        onClick={validateDiscountCode}
                        disabled={isValidating}
                        className="px-4 py-2 rounded-xl font-manrope font-semibold text-sm transition-colors duration-300 hover:bg-white/10 disabled:opacity-50"
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--glass-border)',
                          color: 'var(--text-primary)',
                        }}
                        data-testid="apply-discount-btn"
                      >
                        {isValidating ? '…' : isEnglish ? 'Apply' : 'Einlösen'}
                      </button>
                    </div>
                    <AnimatePresence>
                      {discountError && (
                        <motion.div
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          className="flex items-center gap-1 text-xs mt-2"
                          style={{ color: '#ef4444' }}
                        >
                          <AlertCircle className="w-3 h-3" />
                          {discountError}
                        </motion.div>
                      )}
                      {discountInfo && (
                        <motion.div
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          className="flex items-center gap-1 text-xs mt-2"
                          style={{ color: '#22c55e' }}
                        >
                          <Check className="w-3 h-3" />
                          {isEnglish
                            ? `${discountInfo.discount_percent}% discount applied!`
                            : `${discountInfo.discount_percent}% Rabatt angewendet!`}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Totals — gradient hairline above the Total row marks
                      it as the closing summary. */}
                  <div className="space-y-2">
                    <div
                      className="flex justify-between font-manrope text-sm"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <span>{isEnglish ? 'Subtotal' : 'Zwischensumme'}</span>
                      <span>{preis(getSubtotal())}</span>
                    </div>
                    {discountInfo && (
                      <div
                        className="flex justify-between font-manrope text-sm"
                        style={{ color: '#22c55e' }}
                      >
                        <span className="flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          {isEnglish ? 'Discount' : 'Rabatt'} (
                          {discountInfo.discount_percent}%)
                        </span>
                        <span>−{preis(getDiscountAmount())}</span>
                      </div>
                    )}
                    <div
                      className="pt-3"
                      style={{
                        borderTop:
                          '1px solid transparent',
                        borderImage:
                          'linear-gradient(90deg, transparent 0%, rgba(249,115,22,0.4) 50%, transparent 100%) 1',
                      }}
                    >
                      <div className="flex justify-between font-manrope text-xl font-extrabold">
                        <span style={{ color: 'var(--text-primary)' }}>
                          {isEnglish ? 'Total' : 'Gesamt'}
                        </span>
                        <span style={{ color: 'var(--primary)' }}>
                          {preis(getTotal())}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Legal consent — required before checkout */}
                  <div className="space-y-2.5 mb-4 px-1">
                    <label
                      className="flex items-start gap-2.5 text-xs cursor-pointer select-none"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <Haekchen
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        testId="cart-accept-terms"
                      />
                      <span className="leading-relaxed">
                        {isEnglish ? (
                          <>
                            I have read and accept the{' '}
                            <a
                              href="/en/terms"
                              target="_blank"
                              rel="noopener"
                              style={{ color: 'var(--primary)' }}
                              className="underline"
                            >
                              Terms
                            </a>{' '}
                            and the{' '}
                            <a
                              href="/en/withdrawal"
                              target="_blank"
                              rel="noopener"
                              style={{ color: 'var(--primary)' }}
                              className="underline"
                            >
                              Withdrawal Policy
                            </a>
                            .
                          </>
                        ) : (
                          <>
                            Ich habe die{' '}
                            <a
                              href="/agb"
                              target="_blank"
                              rel="noopener"
                              style={{ color: 'var(--primary)' }}
                              className="underline"
                            >
                              AGB
                            </a>{' '}
                            und die{' '}
                            <a
                              href="/widerruf"
                              target="_blank"
                              rel="noopener"
                              style={{ color: 'var(--primary)' }}
                              className="underline"
                            >
                              Widerrufsbelehrung
                            </a>{' '}
                            gelesen und akzeptiere sie.
                          </>
                        )}
                      </span>
                    </label>
                    <label
                      className="flex items-start gap-2.5 text-xs cursor-pointer select-none"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <Haekchen
                        checked={waiveWithdrawal}
                        onChange={(e) => setWaiveWithdrawal(e.target.checked)}
                        testId="cart-waive-withdrawal"
                      />
                      <span className="leading-relaxed">
                        {isEnglish ? (
                          <>
                            I expressly consent to performance of the contract
                            beginning before the withdrawal period expires and
                            acknowledge that{' '}
                            <strong>my right of withdrawal expires</strong> upon
                            download being made available (§&nbsp;356&nbsp;(6) no.&nbsp;2 BGB).
                          </>
                        ) : (
                          <>
                            Ich stimme ausdrücklich zu, dass mit der Ausführung
                            des Vertrags vor Ablauf der Widerrufsfrist begonnen
                            wird und bestätige, dass{' '}
                            <strong>mein Widerrufsrecht erlischt</strong>, sobald
                            der Download bereitgestellt wird (§&nbsp;356 Abs.&nbsp;6 Nr.&nbsp;2 BGB).
                          </>
                        )}
                      </span>
                    </label>
                  </div>

                  {/* Checkout Button — magnetic on hover, with a shimmer
                      sweep when enabled. Disabled state stays static. */}
                  {isCheckingOut || !acceptTerms || !waiveWithdrawal ? (
                    <button
                      onClick={handleCheckout}
                      disabled
                      className="w-full py-4 rounded-xl font-manrope font-bold text-base text-black opacity-50 cursor-not-allowed flex items-center justify-center gap-2"
                      style={{
                        background: 'var(--primary)',
                      }}
                      data-testid="checkout-btn"
                    >
                      {isCheckingOut ? (
                        <>
                          <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                          {isEnglish ? 'Processing...' : 'Verarbeite...'}
                        </>
                      ) : (
                        <>
                          <ShoppingBag className="w-5 h-5" />
                          {isEnglish ? 'Checkout' : 'Zur Kasse'}
                        </>
                      )}
                    </button>
                  ) : (
                    <MagneticButton strength={0.22} fullWidth>
                      <button
                        onClick={handleCheckout}
                        className="group relative w-full py-4 rounded-xl font-manrope font-bold text-base transition-all duration-300 flex items-center justify-center gap-2 text-black overflow-hidden hover:shadow-[0_0_50px_rgba(249,115,22,0.5)]"
                        style={{
                          background: 'var(--primary)',
                          boxShadow: '0 0 30px rgba(249, 115, 22, 0.3)',
                        }}
                        data-testid="checkout-btn"
                      >
                        {/* Shimmer sweep on hover */}
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
                          style={{
                            background:
                              'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
                          }}
                        />
                        <MagneticLabel className="inline-flex items-center gap-2" style={{ display: 'inline-flex' }}>
                          <ShoppingBag className="w-5 h-5" />
                          {isEnglish ? 'Checkout' : 'Zur Kasse'}
                        </MagneticLabel>
                      </button>
                    </MagneticButton>
                  )}

                  {/* Hinweis nach § 7 Abs. 3 Nr. 4 UWG: Rundmails an Käufer nur, wenn schon beim Kauf
                      auf das Widerspruchsrecht hingewiesen wird (Abmeldelink steht in jeder Rundmail) */}
                  <p className="font-manrope text-[11px] leading-relaxed text-center px-2" style={{ color: 'var(--text-muted)' }}>
                    {isEnglish
                      ? 'After your purchase we occasionally email you news about similar tools. You can object at any time, via the link in every such email or at contact@davidmedia.de.'
                      : 'Nach dem Kauf schicken wir dir ab und zu Neuigkeiten zu ähnlichen Werkzeugen per Mail. Du kannst dem jederzeit widersprechen, über den Link in jeder dieser Mails oder an contact@davidmedia.de.'}
                  </p>

                  {/* Clear Cart */}
                  <button
                    onClick={clearCart}
                    className="w-full py-2 font-manrope text-xs transition-colors duration-300 hover:text-white"
                    style={{ color: 'var(--text-muted)' }}
                    data-testid="clear-cart-btn"
                  >
                    {isEnglish ? 'Clear Cart' : 'Warenkorb leeren'}
                  </button>
                </motion.div>
              )}
            </motion.div>
          </motion.aside>
        </React.Fragment>
      )}
    </AnimatePresence>
  );
};

export default CartSidebar;
